import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { hdrm, dialogs } from '@yola/ws-sdk';
import { mobile } from 'yola-bowser';
import { Modal, Panel, PanelGroup, ButtonGroup, Button, designSystem } from '@yola/ws-ui';

const { Box, Heading } = designSystem;

const DRAG_ELEMENT_CLASS = 'ws-drag-trigger';
const ATTRIBUTES = {
  [hdrm.constants.attributes.DISABLE_BLUR_ATTR]: true,
};

const ModalWithPanels = ({
  width,
  height,
  offsetX: initialOffsetX = 0,
  offsetY: initialOffsetY = 0,
  captions,
  overlay = 'transparent',
  withHorizontalPadding = true,
  draggable = true,
  centered = false,
  fullscreenOnMobile = true,
  elementForPositioning = null,
  floatingModalElementSelector = null,
  children,
  onSubmit,
  onCancel,
  onReady = Function.prototype,
  onPositioningReady = Function.prototype,
  onOverlayClick = Function.prototype,
  onDragEnd = Function.prototype,
  onPressEsc = Function.prototype,
  onKeyDown = Function.prototype,
}) => {
  const modalRef = useRef(null);
  const [offset, setOffset] = useState({ x: initialOffsetX, y: initialOffsetY });

  const handlePositionReady = () => {
    if (!elementForPositioning || (initialOffsetX && initialOffsetY)) {
      return;
    }

    const {
      modal: { width: modalWidth, height: modalHeight },
    } = modalRef.current.state;
    const { offsetX, offsetY } = dialogs.helpers.getPositionByElement(
      elementForPositioning,
      modalWidth,
      modalHeight
    );
    const newOffset = {
      x: offsetX,
      y: offsetY,
    };

    setOffset(newOffset);
    onPositioningReady(newOffset);
  };

  const handleModalReady = () => {
    handlePositionReady();
    onReady();
  };

  const classes = classNames('ws-modal-with-panels', {
    'ws-modal-with-panels--auto-height': height === 'auto',
  });

  return (
    <Modal
      className={classes}
      modalRef={modalRef}
      width={width}
      height={height}
      offsetX={offset.x}
      offsetY={offset.y}
      attributes={ATTRIBUTES}
      overlay={overlay}
      fullscreen={fullscreenOnMobile && mobile}
      floatingModalElementSelector={floatingModalElementSelector}
      resizable={false}
      draggable={draggable}
      centered={centered}
      dragHandleSelector={`.${DRAG_ELEMENT_CLASS}`}
      onReady={handleModalReady}
      onOverlayClick={onOverlayClick}
      onDragEnd={onDragEnd}
      onPressEsc={onPressEsc}
      onKeyDown={onKeyDown}
    >
      <PanelGroup height="100%" className="ws-modal-with-panels__panel-group">
        <Panel
          className={`ws-modal-with-panels__top-panel ${DRAG_ELEMENT_CLASS}`}
          theme="gray-100"
          corners="squared"
          align="middle"
        >
          <Box paddingLeft="spacing-m" paddingRight="spacing-m">
            <Heading type="heading-6" singleLine>
              {captions.title}
            </Heading>
          </Box>
        </Panel>
        <Panel className="ws-modal-with-panels__content-panel" theme="white" scrollable>
          <Box
            paddingTop="spacing-m"
            paddingBottom="spacing-m"
            {...(withHorizontalPadding && {
              paddingLeft: 'spacing-m',
              paddingRight: 'spacing-m',
            })}
          >
            {children}
          </Box>
        </Panel>
        <Panel className="ws-modal-with-panels__bottom-panel" theme="white">
          <ButtonGroup stick="top" block>
            <Button stretch="block" size="large" onClick={onSubmit}>
              {captions.submitButton}
            </Button>
            <Button stretch="block" size="large" onClick={onCancel}>
              {captions.cancelButton}
            </Button>
          </ButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

ModalWithPanels.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]).isRequired,
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  captions: PropTypes.shape({
    title: PropTypes.string.isRequired,
    submitButton: PropTypes.string.isRequired,
    cancelButton: PropTypes.string.isRequired,
  }).isRequired,
  withHorizontalPadding: PropTypes.bool,
  elementForPositioning: PropTypes.oneOfType([PropTypes.shape(), PropTypes.node]),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  centered: PropTypes.bool,
  draggable: PropTypes.bool,
  fullscreenOnMobile: PropTypes.bool,
  overlay: PropTypes.oneOf(['transparent', 'visible', 'none']),
  floatingModalElementSelector: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onReady: PropTypes.func,
  onPositioningReady: PropTypes.func,
  onOverlayClick: PropTypes.func,
  onDragEnd: PropTypes.func,
  onPressEsc: PropTypes.func,
  onKeyDown: PropTypes.func,
};

export default ModalWithPanels;
